import React, { useState } from 'react';
import { Button, Modal, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import CommunicationService from '../services/CommunicationService';

interface FeedbackButtonsProps {
  booking: {
    bookingCode: string;
    customerName: string;
  };
  event: {
    title: string;
    date: string;
  };
  size?: 'sm' | 'lg';
  variant?: 'primary' | 'success' | 'outline-primary' | 'outline-success';
  className?: string;
}

/**
 * Feedback Collection Component
 * Allows users to send feedback about events using backend templates
 */
const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  booking,
  event,
  size,
  variant = 'outline-primary',
  className = ''
}) => {
  const [showModal, setShowModal] = useState(false);
  const [rating, setRating] = useState(5);
  const [feedbackText, setFeedbackText] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleQuickFeedback = async (channel: 'email' | 'whatsapp') => {
    try {
      await CommunicationService.sendFeedback(
        booking.customerName,
        event.title,
        booking.bookingCode,
        event.date,
        channel
      );
    } catch (error) {
      console.error('Error sending feedback:', error);
    }
  };

  const handleDetailedFeedback = async (channel: 'email' | 'whatsapp') => {
    if (!feedbackText.trim()) {
      return;
    }

    try {
      setLoading(true);
      
      // Create detailed feedback message
      const detailedMessage = `Event Rating: ${rating}/5 stars\n\nFeedback:\n${feedbackText}`;
      
      // Use the contact support method with feedback-specific data
      await CommunicationService.contactSupport(
        booking.customerName,
        '', // phone will be filled by user if needed
        'Event Feedback',
        `Event: ${event.title}\nBooking: ${booking.bookingCode}\nDate: ${event.date}\n\n${detailedMessage}`,
        channel
      );
      
      setSuccess(true);
      setTimeout(() => {
        setShowModal(false);
        setSuccess(false);
        setFeedbackText('');
        setRating(5);
      }, 2000);
    } catch (error) {
      console.error('Error sending detailed feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => (
      <Button
        key={index}
        variant="link"
        className="p-0 me-1"
        onClick={() => setRating(index + 1)}
        style={{ 
          color: index < rating ? '#FFD600' : '#dee2e6',
          fontSize: '1.5rem',
          textDecoration: 'none'
        }}
      >
        <i className="bi bi-star-fill"></i>
      </Button>
    ));
  };

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={className}
        onClick={() => setShowModal(true)}
      >
        <i className="bi bi-chat-heart me-1"></i>
        Share Feedback
      </Button>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#810081', color: 'white' }}>
          <Modal.Title>
            <i className="bi bi-chat-heart me-2"></i>
            Event Feedback
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className="p-4">
          {success ? (
            <Alert variant="success" className="text-center">
              <i className="bi bi-check-circle-fill me-2" style={{ fontSize: '2rem' }}></i>
              <h5>Thank You for Your Feedback!</h5>
              <p className="mb-0">Your feedback helps us improve our events.</p>
            </Alert>
          ) : (
            <>
              {/* Event Details */}
              <div className="mb-4 p-3 rounded" style={{ backgroundColor: 'rgba(129, 0, 129, 0.1)' }}>
                <h6 style={{ color: '#810081' }}>{event.title}</h6>
                <small className="text-muted">
                  <i className="bi bi-calendar me-1"></i>{event.date}
                  <span className="ms-3">
                    <i className="bi bi-ticket me-1"></i>{booking.bookingCode}
                  </span>
                </small>
              </div>

              {/* Quick Feedback */}
              <div className="mb-4">
                <h6 style={{ color: '#810081' }}>Quick Feedback</h6>
                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => handleQuickFeedback('whatsapp')}
                    className="flex-fill"
                  >
                    <i className="bi bi-whatsapp me-1"></i>
                    WhatsApp
                  </Button>
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={() => handleQuickFeedback('email')}
                    className="flex-fill"
                  >
                    <i className="bi bi-envelope me-1"></i>
                    Email
                  </Button>
                </div>
                <small className="text-muted">Send a quick feedback message</small>
              </div>

              <hr />

              {/* Detailed Feedback */}
              <div>
                <h6 style={{ color: '#810081' }}>Detailed Feedback</h6>
                
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Rate Your Experience</Form.Label>
                    <div className="d-flex align-items-center">
                      {renderStars()}
                      <span className="ms-2 text-muted">({rating}/5)</span>
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Your Feedback</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      placeholder="Share your experience, suggestions, or any comments about the event..."
                    />
                  </Form.Group>

                  <div className="d-flex gap-2">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => handleDetailedFeedback('whatsapp')}
                      disabled={loading || !feedbackText.trim()}
                      className="flex-fill"
                    >
                      {loading ? <Spinner size="sm" className="me-1" /> : <i className="bi bi-whatsapp me-1"></i>}
                      WhatsApp
                    </Button>
                    <Button
                      variant="warning"
                      size="sm"
                      onClick={() => handleDetailedFeedback('email')}
                      disabled={loading || !feedbackText.trim()}
                      className="flex-fill"
                    >
                      {loading ? <Spinner size="sm" className="me-1" /> : <i className="bi bi-envelope me-1"></i>}
                      Email
                    </Button>
                  </div>
                </Form>
              </div>
            </>
          )}
        </Modal.Body>

        {!success && (
          <Modal.Footer>
            <small className="text-muted me-auto">
              <i className="bi bi-info-circle me-1"></i>
              Your feedback is valuable to us
            </small>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        )}
      </Modal>
    </>
  );
};

export default FeedbackButtons;